import type { UIEvent } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import type {
  ColumnDef,
  OnChangeFn,
  Row,
  SortingState,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'

import ChevronBottomIcon from '@/assets/icons/icon-chevron-bottom.svg'
import { IconLoading } from '@/assets/icons/icon-loading'
import { EmptyTableContent } from '@/components/ui/empty/empty-table-content'
import type { MassMessage } from '@/modules/chatting/types'
import { classNames } from '@/utils/helpers'

import { VirtualRow } from './VirtualRow.tsx'

interface Props {
  data: MassMessage[]
  onLoadMore: () => void
  canLoadMore: boolean
  isLoading: boolean
  isLoadingMore: boolean
  columns: ColumnDef<MassMessage>[]
}

export const StatsTable = ({
  onLoadMore,
  canLoadMore,
  data,
  isLoading,
  isLoadingMore,
  columns,
}: Props) => {
  const [sorting, setSorting] = useState<SortingState>([])
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement

        if (scrollHeight - scrollTop - clientHeight < 100 && canLoadMore) {
          onLoadMore()
        }
      }
    },
    [onLoadMore, canLoadMore],
  )

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current)
  }, [fetchMoreOnBottomReached])

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
    debugTable: true,
  })

  const { rows } = table.getRowModel()

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 40,
    getScrollElement: () => tableContainerRef.current,
    measureElement:
      typeof window !== 'undefined' &&
      navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  })

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      setSorting(updater)
      if (table.getRowModel().rows.length) {
        rowVirtualizer.scrollToIndex?.(0)
      }
    },
    [table, rowVirtualizer],
  )

  useEffect(() => {
    table.setOptions((prev) => ({
      ...prev,
      onSortingChange: handleSortingChange,
    }))
  }, [handleSortingChange, table])

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    fetchMoreOnBottomReached(event.target as HTMLDivElement)
  }

  if (isLoading) {
    return (
      <div className="bg-card p-4">
        <IconLoading className="mx-auto my-3 animate-spin" />
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <EmptyTableContent>
        There are no mass messages in selected category.
      </EmptyTableContent>
    )
  }

  return (
    <div
      onScroll={handleScroll}
      ref={tableContainerRef}
      className="relative h-[493px] overflow-auto"
    >
      <table className="grid">
        {/* Table Header */}
        <thead className="sticky top-0 z-10 grid">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              key={headerGroup.id}
              className="bg-card flex w-full border-b py-2"
            >
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className="text-secondary-300 flex p-2 text-sm font-medium"
                  style={{
                    width: header.getSize(),
                  }}
                >
                  <div
                    className={classNames(
                      'flex items-center',
                      header.column.getCanSort() &&
                        'cursor-pointer select-none',
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    {header.column.getCanSort() && (
                      <div className="ml-2 flex flex-col items-center">
                        <ChevronBottomIcon
                          className={classNames(
                            'stroke-secondary-500 dark:stroke-secondary-100 h-[8px] w-[16px] rotate-180',
                            {
                              'stroke-primary-500 dark:stroke-primary-500':
                                header.column.getIsSorted() === 'asc',
                            },
                          )}
                        />
                        <ChevronBottomIcon
                          className={classNames(
                            'stroke-secondary-500 dark:stroke-secondary-100 h-[8px] w-[16px]',
                            {
                              'stroke-primary-500 dark:stroke-primary-500':
                                header.column.getIsSorted() === 'desc',
                            },
                          )}
                        />
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        {/* Table Body */}
        <tbody
          className="relative grid"
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => (
            <VirtualRow
              key={rows[virtualRow.index].id}
              virtualRow={virtualRow}
              row={rows[virtualRow.index] as Row<MassMessage>}
              measureElement={rowVirtualizer.measureElement}
            />
          ))}
        </tbody>
      </table>
      {isLoadingMore && (
        <div className="bg-card sticky bottom-0 left-0 right-0 py-3">
          <IconLoading className="mx-auto animate-spin" />
        </div>
      )}
    </div>
  )
}
